import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { Label } from 'core/components/label';
import { Tag } from 'core/components/tag';
import { capitalize, dateTimeFormatter } from 'core/helpers/formatters';
import { Grid } from '@material-ui/core';
import { policyTypeNames } from '@ourbranch/policy-types';

import editIcon from 'core/assets/svg/edit-square.svg';
import uploadIcon from '../../assets/upload.svg';
import completedIcon from '../../assets/completed.svg';
import arrowIcon from 'core/assets/svg/up-right-arrow.svg';

import { getTaskDisplayName, appcuesModalIdsByTaskName } from '../../helpers/task-helpers';

import useStyles from './task.styles.js';

/**
 * Offer Task component - simplified for offer-only use
 */
const Task = ({ taskId, taskName, data, files, completed, completedDate, readOnly, completedBy, onOpenModal }) => {
  const classes = useStyles();
  const appcuesModalId = appcuesModalIdsByTaskName[taskName];

  const handleOpenUploadModal = () => {
    if (onOpenModal) {
      onOpenModal(taskName, taskId, files);
    }
  };

  return (
    <Grid container spacing={4} justifyContent="space-between" alignItems="center" className={classes.container}>
      <Grid item className={classes.task}>
        {completed && <img alt="rounded checkmark" className={classes.completedIcon} src={completedIcon} />}
        <div>
          <div className={classNames(classes.taskInfo, { [classes.taskInfoCompleted]: completed })}>
            <div className={classes.taskNameContainer}>
              <Label type="infoValue" className={classNames(classes.taskName, { [classes.completedTask]: completed })}>
                {getTaskDisplayName(taskName)}
              </Label>
              {appcuesModalId && (
                <IconButton
                  className={classes.learnMoreLink}
                  onClick={() => {
                    window?.Appcues?.show(appcuesModalId);
                  }}
                >
                  <div className={classes.learnMoreLinkText}>Learn More</div>
                  <img src={arrowIcon} height={14} width={14} alt="Learn More" />
                </IconButton>
              )}
            </div>
            {completedDate && (
              <Label variant="darkGreenSmall" className={classes.completedAt}>
                Submitted on {dateTimeFormatter(completedDate)} EST by {completedBy}
              </Label>
            )}
          </div>
          <div className={classes.tags}>
            <Tag className={classes.tag}>
              {data.policyType && policyTypeNames[data.policyType]
                ? capitalize(policyTypeNames[data.policyType])
                : 'Offer Task'}
              {data?.driver && ` - ${data.driver.firstName} ${data.driver.lastName}`}
              {data?.customer && !data?.driver && ` - ${data.customer}`}
            </Tag>
          </div>
        </div>
      </Grid>
      {completed ? (
        <div className={classes.task}>
          <div className={classes.iconButtons}>
            {!readOnly && (
              <Button color="secondary" onClick={handleOpenUploadModal} className={classes.actionButton}>
                <img src={editIcon} alt="edit" className={classes.actionButtonIcon} />
                Edit
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div>
          {!readOnly && (
            <Button
              onClick={handleOpenUploadModal}
              disableFocusRipple
              className={classes.upload}
              variant="text"
              color="secondary"
            >
              <img alt="upload icon" className={classNames(classes.uploadIcon)} src={uploadIcon} />
              Upload
            </Button>
          )}
        </div>
      )}
    </Grid>
  );
};

Task.propTypes = {
  taskId: PropTypes.string.isRequired,
  taskName: PropTypes.string.isRequired,
  data: PropTypes.object,
  files: PropTypes.array,
  completed: PropTypes.bool,
  completedDate: PropTypes.string,
  entityId: PropTypes.string.isRequired,
  entityType: PropTypes.oneOf(['offer']).isRequired,
  readOnly: PropTypes.bool,
  completedBy: PropTypes.string,
  onUpload: PropTypes.func,
  onDownload: PropTypes.func,
  onDelete: PropTypes.func,
  onOpenModal: PropTypes.func
};

Task.defaultProps = {
  data: {},
  files: [],
  completedDate: null,
  readOnly: false
};

export default observer(Task);
